import { RoleLevel, getInheritedPermissions, ROLE_LABELS } from '../types/rbacImproved';
export class PermissionService {
    /**
     * Calculate final permissions for a user including overrides
     */
    static calculateUserPermissions(baseRole, overrides) {
        // Start with inherited permissions from role
        let permissions = getInheritedPermissions(baseRole);
        if (overrides) {
            // Add additional permissions
            permissions.push(...overrides.additionalPermissions);
            // Add active temporary permissions
            const activeTemporaryPermissions = overrides.temporaryPermissions
                .filter(tp => tp.isActive && new Date() < tp.expiresAt)
                .map(tp => tp.permission);
            permissions.push(...activeTemporaryPermissions);
            // Remove revoked permissions
            permissions = permissions.filter(p => !overrides.revokedPermissions.includes(p));
        }
        // Remove duplicates and return
        return [...new Set(permissions)];
    }
    /**
     * Check if user has specific permission
     */
    static hasPermission(userPermissions, requiredPermission) {
        return userPermissions.includes(requiredPermission);
    }
    /**
     * Check if user has any of the required permissions
     */
    static hasAnyPermission(userPermissions, requiredPermissions) {
        return requiredPermissions.some(permission => this.hasPermission(userPermissions, permission));
    }
    /**
     * Check if user has all required permissions
     */
    static hasAllPermissions(userPermissions, requiredPermissions) {
        return requiredPermissions.every(permission => this.hasPermission(userPermissions, permission));
    }
    /**
     * Check if user can access a specific page
     */
    static canAccessPage(userPermissions, pagePermission) {
        return this.hasPermission(userPermissions, pagePermission);
    }
    /**
     * Check if user can use a specific component
     */
    static canUseComponent(userPermissions, componentPermission) {
        return this.hasPermission(userPermissions, componentPermission);
    }
    /**
     * Check if user can perform a specific action
     */
    static canPerformAction(userPermissions, actionPermission) {
        return this.hasPermission(userPermissions, actionPermission);
    }
    /**
     * Check if access is allowed based on restrictions
     */
    static checkAccessRestrictions(restrictions, context) {
        const now = context.timestamp || new Date();
        // Check IP restrictions
        if (restrictions.ipWhitelist && context.ip) {
            const isAllowedIP = restrictions.ipWhitelist.some(allowedIP => {
                // Support CIDR notation in the future
                return context.ip === allowedIP || context.ip?.startsWith(allowedIP);
            });
            if (!isAllowedIP) {
                return { allowed: false, reason: 'IP address not in whitelist' };
            }
        }
        // Check time restrictions
        if (restrictions.timeRestrictions) {
            const { allowedHours, allowedDays, timezone } = restrictions.timeRestrictions;
            // Convert to user's timezone
            const userTime = new Date(now.toLocaleString("en-US", { timeZone: timezone }));
            const currentHour = userTime.getHours();
            const currentDay = userTime.getDay();
            if (!allowedHours.includes(currentHour)) {
                return { allowed: false, reason: 'Access not allowed at this time' };
            }
            if (!allowedDays.includes(currentDay)) {
                return { allowed: false, reason: 'Access not allowed on this day' };
            }
        }
        // Check location restrictions
        if (restrictions.locationRestrictions && context.location) {
            if (!restrictions.locationRestrictions.includes(context.location)) {
                return { allowed: false, reason: 'Access not allowed from this location' };
            }
        }
        // Check device restrictions
        if (restrictions.deviceRestrictions?.allowedDevices && context.deviceFingerprint) {
            if (!restrictions.deviceRestrictions.allowedDevices.includes(context.deviceFingerprint)) {
                return { allowed: false, reason: 'Device not authorized' };
            }
        }
        return { allowed: true };
    }
    /**
     * Get role level from role name
     */
    static getRoleLevel(roleName) {
        const roleNameUpper = roleName.toUpperCase();
        // Handle legacy role names and variations
        if (roleNameUpper === 'DEALER' || roleNameUpper === 'DEALERS') {
            return RoleLevel.DEALER_APPLICANT; // Default dealer to applicant level
        }
        const roleKey = roleNameUpper;
        return RoleLevel[roleKey] || RoleLevel.GUEST;
    }
    /**
     * Check if user role can manage another user role
     */
    static canManageRole(managerRole, targetRole) {
        // Can only manage roles lower than your own
        return managerRole > targetRole;
    }
    /**
     * Get maximum role a user can assign
     */
    static getMaxAssignableRole(userRole) {
        // Can assign any role lower than your own
        return Math.max(userRole - 1, RoleLevel.SUSPENDED);
    }
    /**
     * Validate permission grant request
     */
    static validatePermissionGrant(granterRole, targetRole, requestedPermission) {
        // Only higher roles can grant permissions
        if (granterRole <= targetRole) {
            return { valid: false, reason: 'Insufficient authority to grant permissions' };
        }
        // Check if granter has the permission they're trying to grant
        const granterPermissions = getInheritedPermissions(granterRole);
        if (!granterPermissions.includes(requestedPermission)) {
            return { valid: false, reason: 'Cannot grant permission you do not possess' };
        }
        return { valid: true };
    }
    /**
     * Clean up expired temporary permissions
     */
    static cleanupExpiredPermissions(temporaryPermissions) {
        const now = new Date();
        return temporaryPermissions.map(tp => ({
            ...tp,
            isActive: tp.isActive && now < tp.expiresAt
        }));
    }
    /**
     * Get user-friendly permission description
     */
    static getPermissionDescription(permission) {
        const descriptions = {
            // Page permissions
            'page.atelier.view': 'Access to Diamond Trading Platform',
            'page.admin.dashboard.view': 'View Admin Dashboard',
            'page.admin.users.view': 'Access User Management',
            // Component permissions
            'component.inventory.premium.view': 'View Advanced Diamond Inventory',
            'component.search.advanced': 'Use Advanced Search Features',
            'component.memo.request': 'Request Diamond Memos',
            // Action permissions
            'action.diamond.purchase': 'Purchase Diamonds',
            'action.admin.users.manage': 'Manage User Accounts',
            'action.admin.pricing.update': 'Update Diamond Pricing'
        };
        return descriptions[permission] || permission.replace(/[._]/g, ' ').toUpperCase();
    }
    /**
     * Generate permission report for user
     */
    static generatePermissionReport(userId, baseRole, overrides) {
        const finalPermissions = this.calculateUserPermissions(baseRole, overrides);
        const inheritedPermissions = getInheritedPermissions(baseRole);
        return {
            userId,
            baseRole,
            roleLevel: baseRole,
            roleName: ROLE_LABELS[baseRole],
            permissions: {
                total: finalPermissions.length,
                inherited: inheritedPermissions.length,
                additional: overrides?.additionalPermissions.length || 0,
                revoked: overrides?.revokedPermissions.length || 0,
                temporary: overrides?.temporaryPermissions.filter(tp => tp.isActive).length || 0
            },
            breakdown: {
                pages: finalPermissions.filter(p => p.startsWith('page.')),
                components: finalPermissions.filter(p => p.startsWith('component.')),
                actions: finalPermissions.filter(p => p.startsWith('action.'))
            },
            generatedAt: new Date()
        };
    }
}
// Export commonly used functions
export const { calculateUserPermissions, hasPermission, hasAnyPermission, hasAllPermissions, canAccessPage, canUseComponent, canPerformAction, checkAccessRestrictions, getRoleLevel, canManageRole, getMaxAssignableRole, validatePermissionGrant, cleanupExpiredPermissions, getPermissionDescription, generatePermissionReport } = PermissionService;
